"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { X, RotateCcw, ChevronRight, Check } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useMedia } from "react-use";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";
import {
  getLevel1Categories,
  getLevel2Categories,
  getLevel3Categories,
  getPromotedCategories,
  getCategoryByCode,
  getCategoryName,
  type CategoryItem,
} from "@/config/categories";
import { getCategoryPhosphorIcon } from "@/components/home/variants/categoryPhosphorIcons";
import { getCategoryRailName } from "@/config/category-rail-names";
import { PromotedCategoryRow } from "./PromotedCategoryRow";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import type { Locale } from "@/i18n/routing";

type LocaleKey = Locale;

/** 레일 클릭 직후 스크롤-스파이가 중간 섹션으로 튀지 않게 잠그는 시간(ms) */
const SPY_LOCK_MS = 700;

interface CategorySheetCommonProps {
  open: boolean;
  onClose: () => void;
  /** 시트 제목 — 기본값은 `search.categorySheet.title` */
  title?: string;
}

interface CategorySheetSelectProps extends CategorySheetCommonProps {
  mode: "select";
  selectedCategories: string[];
  onApply: (categories: string[]) => void;
}

interface CategorySheetNavigateProps extends CategorySheetCommonProps {
  mode: "navigate";
  /** 타일·전체 버튼 클릭 — 호출부가 이동·닫기를 처리한다 */
  onNavigate: (code: string) => void;
}

export type CategorySheetProps =
  | CategorySheetSelectProps
  | CategorySheetNavigateProps;

/* ------------------------------------------------------------------ */
/* 타일                                                                */
/* ------------------------------------------------------------------ */

interface TileProps {
  category: CategoryItem;
  /** 호출부가 getCategoryPhosphorIcon 으로 구해 넘긴다(L3→L2→L1 폴백) */
  icon: PhosphorIcon;
  localeKey: LocaleKey;
  isDesktop: boolean;
  selected: boolean;
  onClick: () => void;
}

function Tile({
  category,
  icon: Icon,
  localeKey,
  isDesktop,
  selected,
  onClick,
}: TileProps) {
  const circleSize = isDesktop ? 64 : 54;
  const iconSize = isDesktop ? 29 : 25;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 min-w-0 cursor-pointer"
    >
      <span
        style={{ width: circleSize, height: circleSize }}
        className={cn(
          "flex items-center justify-center rounded-full transition-all",
          selected
            ? "bg-white text-orange-300 ring-2 ring-gray-900"
            : "bg-white text-orange-300 ring-1 ring-gray-200",
        )}
      >
        <Icon size={iconSize} weight="duotone" color="currentColor" />
      </span>
      <span
        className={cn(
          "text-center line-clamp-2 break-keep w-full",
          isDesktop ? "text-[12px] leading-3.75" : "text-[11px] leading-3.5",
          selected
            ? "text-gray-900 font-semibold"
            : "text-gray-800 font-medium",
        )}
      >
        {getCategoryName(category, localeKey)}
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* 본문 (헤더 · 추천 줄 · 레일 + 패널 · 푸터)                            */
/* ------------------------------------------------------------------ */

interface SheetBodyProps {
  props: CategorySheetProps;
  isDesktop: boolean;
  title: string;
}

/**
 * 시트 본문. 임시 선택(select 모드)을 여기서 소유하고 초기값을 props 에서 바로 읽는다.
 * Radix Dialog / vaul Drawer 는 닫히면 content 를 언마운트하므로 열릴 때마다 새 마운트
 * = 최신 적용값으로 초기화된다 (open 동기화 effect 불필요 — CategoryFilterDialog 와 동일).
 */
function SheetBody({ props, isDesktop, title }: SheetBodyProps) {
  const { mode, onClose } = props;
  const locale = useLocale();
  const localeKey = locale as LocaleKey;
  const t = useTranslations("search");

  const [tempSelected, setTempSelected] = useState<string[]>(
    mode === "select" ? props.selectedCategories : [],
  );

  const level1Categories = getLevel1Categories();
  const promotedCategories = getPromotedCategories();
  const l2Sections = level1Categories.flatMap((l1) =>
    getLevel2Categories(l1.code),
  );

  const [activeL2, setActiveL2] = useState<string | null>(
    l2Sections[0]?.code ?? null,
  );

  // 패널 · 섹션 · 레일 항목 DOM 참조
  const panelRef = useRef<HTMLDivElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef(new Map<string, HTMLElement>());
  const railItemRefs = useRef(new Map<string, HTMLButtonElement>());
  const spyLockedRef = useRef(false);
  const spyLockTimerRef = useRef<number | null>(null);

  const setSectionRef = useCallback(
    (code: string) => (el: HTMLElement | null) => {
      if (el) sectionRefs.current.set(code, el);
      else sectionRefs.current.delete(code);
    },
    [],
  );
  const setRailItemRef = useCallback(
    (code: string) => (el: HTMLButtonElement | null) => {
      if (el) railItemRefs.current.set(code, el);
      else railItemRefs.current.delete(code);
    },
    [],
  );

  // 스크롤-스파이: 패널을 root 로, 상단 30% 띠에 걸린 섹션 중 가장 위 것을 활성으로.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const visible = new Set<string>();
    const pickTopmost = () => {
      if (spyLockedRef.current) return;
      let best: { code: string; top: number } | null = null;
      for (const code of visible) {
        const el = sectionRefs.current.get(code);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (!best || top < best.top) best = { code, top };
      }
      if (best) setActiveL2(best.code);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const code = (entry.target as HTMLElement).dataset.l2Code;
          if (!code) continue;
          if (entry.isIntersecting) visible.add(code);
          else visible.delete(code);
        }
        pickTopmost();
      },
      { root: panel, rootMargin: "0px 0px -70% 0px", threshold: 0 },
    );
    for (const el of sectionRefs.current.values()) observer.observe(el);

    // 마지막 섹션이 짧아 상단 30% 띠에 못 닿는 경우 — 끝까지 내리면 마지막을 활성으로.
    const onScroll = () => {
      if (spyLockedRef.current) return;
      const atBottom =
        panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 2;
      if (!atBottom) return;
      const lastCode = Array.from(sectionRefs.current.keys()).pop();
      if (lastCode) setActiveL2(lastCode);
    };
    panel.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      panel.removeEventListener("scroll", onScroll);
      if (spyLockTimerRef.current !== null) {
        window.clearTimeout(spyLockTimerRef.current);
      }
    };
  }, []);

  // 활성 레일 항목이 레일 스크롤 안에 보이도록. 첫 렌더는 건너뛴다 — 열자마자
  // 첫 항목으로 스크롤하면 그 위의 대분류 캡션(여행·액티비티)이 레일 밖으로 밀려 안 보인다.
  // scrollIntoView 는 overflow-hidden 인 시트 본문까지 조상 전부를 스크롤시켜 헤더·추천 줄이
  // 위로 밀리므로, 레일의 scrollTop 만 직접 움직인다.
  const railScrollArmedRef = useRef(false);
  useEffect(() => {
    if (!activeL2) return;
    if (!railScrollArmedRef.current) {
      railScrollArmedRef.current = true;
      return;
    }
    const rail = railRef.current;
    const item = railItemRefs.current.get(activeL2);
    if (!rail || !item) return;
    // 위로 올라갈 때는 대분류 캡션까지 같이 보이게 — 그룹의 첫 항목이면 캡션 위치로,
    // 전체 첫 항목이면 맨 위(0)로. 아니면 캡션이 가려진 채 멈춘다.
    const group = item.closest<HTMLElement>("[data-rail-group]");
    const isFirstInGroup = group?.querySelector("button") === item;
    const revealTop = isFirstInGroup
      ? group?.offsetTop ?? item.offsetTop
      : item.offsetTop;
    const groups = rail.querySelectorAll("[data-rail-group]");
    const top = group === groups[0] && isFirstInGroup ? 0 : revealTop;
    // 전체 마지막 항목이면 레일도 끝까지(하단 패딩 포함) 내린다.
    const lastGroup = groups[groups.length - 1];
    const isLastOverall =
      group === lastGroup &&
      lastGroup?.querySelector("button:last-of-type") === item;
    const bottom = isLastOverall
      ? rail.scrollHeight
      : item.offsetTop + item.offsetHeight;
    if (top < rail.scrollTop) {
      rail.scrollTo({ top, behavior: "smooth" });
    } else if (bottom > rail.scrollTop + rail.clientHeight) {
      rail.scrollTo({ top: bottom - rail.clientHeight, behavior: "smooth" });
    }
  }, [activeL2]);

  const scrollToSection = (code: string) => {
    const panel = panelRef.current;
    const section = sectionRefs.current.get(code);
    if (!panel || !section) return;
    spyLockedRef.current = true;
    if (spyLockTimerRef.current !== null) {
      window.clearTimeout(spyLockTimerRef.current);
    }
    spyLockTimerRef.current = window.setTimeout(() => {
      spyLockedRef.current = false;
      spyLockTimerRef.current = null;
    }, SPY_LOCK_MS);
    setActiveL2(code);
    // 패널(relative)을 기준으로 한 offsetTop 만 쓴다 — scrollIntoView 는 fixed 시트 뒤의
    // 페이지까지 스크롤할 수 있어 패널 자체를 직접 스크롤한다.
    const paddingTop = parseFloat(getComputedStyle(panel).paddingTop) || 0;
    panel.scrollTo({
      top: Math.max(0, section.offsetTop - paddingTop),
      behavior: "smooth",
    });
  };

  // 중분류 "전체"가 선택돼 있으면 그 소분류는 모두 선택된 것으로 보인다(10-01).
  const parentOf = (code: string) => {
    const category = getCategoryByCode(code);
    return category?.level === 3 ? category.parent_code ?? null : null;
  };

  const toggle = (code: string) =>
    setTempSelected((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      const parent = parentOf(code);
      // "전체" 상태에서 소분류를 누르면 그것만 빠진다 — 중분류 대신 나머지 형제를 고른다.
      if (parent && prev.includes(parent)) {
        const siblings = getLevel3Categories(parent)
          .map((c) => c.code)
          .filter((c) => c !== code);
        return [
          ...prev.filter((c) => c !== parent && !siblings.includes(c)),
          ...siblings,
        ];
      }
      // "전체"를 고르면 따로 골라 둔 소분류는 거둔다 — 다시 누르면 모두 꺼지게.
      const children = getLevel3Categories(code).map((c) => c.code);
      return [...prev.filter((c) => !children.includes(c)), code];
    });

  const handleTile = (code: string) => {
    if (mode === "navigate") props.onNavigate(code);
    else toggle(code);
  };

  const isSelected = (code: string) => {
    if (mode !== "select") return false;
    if (tempSelected.includes(code)) return true;
    const parent = parentOf(code);
    return parent !== null && tempSelected.includes(parent);
  };

  const gutter = isDesktop ? "px-7" : "px-4";

  /* ---------- 헤더 ---------- */
  const header = isDesktop ? (
    <div className="flex items-center justify-between px-7 py-4">
      <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      <button
        type="button"
        onClick={onClose}
        className="p-1.5 hover:bg-gray-100 rounded-full transition"
      >
        <X className="w-5 h-5 text-gray-400" />
      </button>
    </div>
  ) : (
    <div className="px-4 pb-2 text-center">
      <h2 className="text-base font-bold text-gray-900">{title}</h2>
    </div>
  );

  /* ---------- 추천 줄 (고정) ---------- */
  const promotedRow =
    promotedCategories.length > 0 ? (
      <div className={cn(gutter, "pt-3 pb-4 shrink-0")}>
        <div className="text-[13px] font-semibold text-gray-900 mb-3">
          {"🔥 "}
          {t("promotedCategories")}
        </div>
        {/* 검색바 "무엇을" 칸과 같은 공용 줄(3D 아트·짧은 라벨·좌우 드래그, 10-15).
            드로어 끌어내리기와 가로 드래그가 겹치지 않게 vaul 드래그를 끈다. */}
        <div data-vaul-no-drag>
          <PromotedCategoryRow
            mobile={!isDesktop}
            isSelected={isSelected}
            onSelect={handleTile}
            wrapperClassName={isDesktop ? "-mx-7" : "-mx-4"}
            className={isDesktop ? "px-7" : "px-4"}
          />
        </div>
      </div>
    ) : null;

  /* ---------- 레일 ---------- */
  // 목차형: 흰 바탕 + 오른쪽 경계선, 대분류는 굵은 검정 캡션(클릭 X), 중분류는 여백 없이
  // 맞붙인 연회색 행 + 흰 구분선, 활성 = 주황 행(10-15). 모바일은 안쪽 여백만 줄인다.
  const railItemPad = isDesktop ? "px-2.5" : "px-2";
  // 데스크탑은 다이얼로그 폭이 넓어 13px 이 작아 보인다 — 크기만 한 단계 올린다.
  const railCaptionText = isDesktop
    ? "text-[12px] leading-[15px]"
    : "text-[11px] leading-[14px]";
  const railItemText = isDesktop
    ? "text-[14px] leading-[19px] py-2"
    : "text-[13px] leading-[17px] py-[7px]";
  const rail = (
    <div
      ref={railRef}
      className={cn(
        "relative shrink-0 overflow-y-auto bg-white border-r border-gray-100 py-3 scrollbar-hide",
        isDesktop ? "w-44" : "w-27",
      )}
    >
      {level1Categories.map((l1, gi) => (
        <div key={l1.code} data-rail-group>
          <div
            className={cn(
              "pb-1.5 font-bold text-gray-900 break-keep",
              railCaptionText,
              railItemPad,
              gi === 0 ? "pt-1" : "pt-4",
            )}
          >
            {getCategoryRailName(l1, localeKey)}
          </div>
          {/* 맞붙인 연회색 타일 + 흰 구분선(10-15) — 레일 폭을 꽉 채운다 */}
          <div className="flex flex-col divide-y divide-white">
            {getLevel2Categories(l1.code).map((l2) => {
              const active = activeL2 === l2.code;
              return (
                <button
                  key={l2.code}
                  ref={setRailItemRef(l2.code)}
                  type="button"
                  onClick={() => scrollToSection(l2.code)}
                  className={cn(
                    "w-full text-left break-keep cursor-pointer transition-colors",
                    railItemText,
                    railItemPad,
                    active
                      // 굵기 위계: 대분류 캡션 700 > 중분류 400(활성도 400, 주황 바탕으로만 구분)
                      ? "bg-orange-500 text-white font-normal"
                      : "bg-gray-100 text-gray-800 font-normal hover:bg-gray-200",
                  )}
                >
                  {getCategoryRailName(l2, localeKey)}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  /* ---------- 패널 ---------- */
  const panel = (
    <div
      ref={panelRef}
      data-vaul-no-drag
      className={cn(
        "relative flex-1 min-w-0 overflow-y-auto",
        isDesktop ? "px-7 pt-5" : "px-4 pt-4",
        // 마지막 섹션이 푸터(또는 시트 끝)에 붙지 않게. 드로어 자체의 safe-area 패딩은 globals 가 준다.
        mode === "select" ? "pb-8" : "pb-10",
      )}
    >
      {l2Sections.map((l2, index) => {
        const l3Categories = getLevel3Categories(l2.code);
        const l2Selected = isSelected(l2.code);
        return (
          <section
            key={l2.code}
            ref={setSectionRef(l2.code)}
            data-l2-code={l2.code}
            className={index > 0 ? "mt-7" : undefined}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[15px] font-semibold text-gray-900">
                {getCategoryName(l2, localeKey)}
              </h3>
              <button
                type="button"
                onClick={() => handleTile(l2.code)}
                className={cn(
                  "inline-flex items-center gap-0.5 text-[12px] cursor-pointer",
                  l2Selected
                    ? "text-gray-700 font-medium"
                    : "text-gray-400 font-normal",
                )}
              >
                {l2Selected
                  ? t("categorySheet.allSelected")
                  : t("categorySheet.all")}
                {l2Selected ? (
                  <Check className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
            {l3Categories.length > 0 && (
              <div
                className={cn(
                  isDesktop
                    ? "flex flex-wrap gap-x-4 gap-y-5 [&>button]:w-21"
                    : "grid grid-cols-3 gap-x-3 gap-y-4",
                )}
              >
                {l3Categories.map((l3) => (
                  <Tile
                    key={l3.code}
                    category={l3}
                    icon={getCategoryPhosphorIcon(l3.code)}
                    localeKey={localeKey}
                    isDesktop={isDesktop}
                    selected={isSelected(l3.code)}
                    onClick={() => handleTile(l3.code)}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );

  /* ---------- 푸터 (select 모드) ---------- */
  const footer =
    mode === "select" ? (
      <div className="px-7 border-t border-gray-100 bg-white shrink-0">
        <div className="flex gap-3 items-center h-18">
          <Button
            variant="ghost"
            onClick={() => setTempSelected([])}
            className="shrink-0 h-12 text-gray-500 hover:text-gray-700 hover:bg-transparent gap-1"
          >
            <RotateCcw className="w-4 h-4" />
            {t("reset")}
          </Button>
          <Button
            onClick={() => {
              props.onApply(tempSelected);
              onClose();
            }}
            className="flex-1 h-12 bg-orange-500 text-white hover:bg-orange-600 rounded-xl text-base"
          >
            {/* 고정 문구(09-23) — in-memory 건수는 카테고리를 바꾸면 0으로 나왔다 */}
            {t("applyFilters")}
          </Button>
        </div>
      </div>
    ) : null;

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-0 overflow-hidden">
      {header}
      {promotedRow}
      {/* 추천 줄과 레일·패널 사이 구분선: 폭 전체(레일 경계선과 만나도록 여백 없이).
          시트는 transform 으로 배치돼 반픽셀에 걸릴 수 있고, 스크롤 중인 레일·패널은 합성
          레이어로 승격되며 정수 픽셀로 스냅된다 — 그때 스크롤러가 부모의 1px 선을 1px 덮어
          선이 깜빡인다. 선 아래 1px 패딩을 두어 스크롤러가 선이 아닌 패딩을 덮게 한다. */}
      <div className="flex flex-1 min-h-0 border-t border-gray-200 pt-px">
        {rail}
        {panel}
      </div>
      {footer}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 셸 (모바일 Drawer / 데스크탑 Dialog)                                  */
/* ------------------------------------------------------------------ */

export default function CategorySheet(props: CategorySheetProps) {
  const { open, onClose } = props;
  const t = useTranslations("search");
  const isDesktop = useMedia("(min-width: 768px)", true);
  const title = props.title ?? t("categorySheet.title");

  const body = <SheetBody props={props} isDesktop={isDesktop} title={title} />;

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          className="sm:max-w-2xl! p-0 bg-white flex flex-col gap-0 overflow-hidden rounded-2xl max-h-[80vh]"
          showCloseButton={false}
        >
          <VisuallyHidden>
            <DialogTitle>{title}</DialogTitle>
          </VisuallyHidden>
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onClose}>
      <DrawerContent className="p-0 bg-white max-h-[85vh]">
        <VisuallyHidden>
          <DrawerTitle>{title}</DrawerTitle>
        </VisuallyHidden>
        {body}
      </DrawerContent>
    </Drawer>
  );
}
